import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../shared/Button";
import {
  checkMyRegistrationStatus,
  getEventForRegistration,
} from "../../hooks/useRegistration";
import "./RegistrationPage.css";
import RegistrationConfirmation from "./RegistrationConfirmation";
import RegistrationForm from "./RegistrationForm";
import { EventSummaryDTO } from "../../types/event.types";
import { EventRegistration } from "../../types/shared.types";

const RegistrationPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventSummaryDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [completedRegistration, setCompletedRegistration] =
    useState<EventRegistration | null>(null);

  const [existingRegistration, setExistingRegistration] = useState<{
    registrationId: string;
    status: string;
    mealPreference: string;
    userName: string;
    employeeId?: string;
    sector?: string;
    cluster?: string;
    notes?: string;
    guestCount?: number;
    additionalAdults?: number;
    additionalChildren?: number;
  } | null>(null);

  const [isEditing, setIsEditing] = useState(false);

  const fetchEventData = useCallback(async () => {
    if (!eventId) return;

    setIsLoading(true);
    setError(null);

    const response = await getEventForRegistration(eventId);

    if (response.success && response.data) {
      setEvent(response.data);

      if (response.data.status === "Cancelled")
        setError("This event has been cancelled.");
      else if (response.data.status === "Completed")
        setError("This event has already ended.");
      else if (response.data.isFull) setError("This event is fully booked.");
      else if (response.data.isDeadlinePassed)
        setError("Registration deadline has passed.");
    } else {
      setError(response.error || "Event not found");
    }
    setIsLoading(false);
  }, [eventId]);

  useEffect(() => {
    if (!eventId) {
      setError("Event ID is missing");
      setIsLoading(false);
      return;
    }
    fetchEventData();
  }, [eventId, fetchEventData]);

  useEffect(() => {
    const run = async () => {
      if (!eventId) return;

      const email = sessionStorage.getItem("userEmail")?.trim().toLowerCase();
      if (!email) return;

      const res = await checkMyRegistrationStatus(eventId);

      if (res.success && res.data?.isRegistered && res.data.registration) {
        setExistingRegistration(res.data.registration);
      } else {
        setExistingRegistration(null);
      }
    };

    run();
  }, [eventId]);

  const handleRegistrationSuccess = (registration: EventRegistration) => {
    setCompletedRegistration(registration);
    setRegistrationComplete(true);
  };

  const formatEventDate = (isoDate: string): string =>
    new Date(isoDate).toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatDeadline = (isoDate: string): string =>
    new Date(isoDate).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (isLoading) {
    return (
      <div className="registration-page">
        <div className="registration-container">
          <div className="registration-loading">
            <div className="registration-spinner" />
            <p>Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="registration-page">
        <div className="registration-container">
          <div className="registration-error">
            <div className="error-icon">⚠️</div>
            <h2>Unable to Load Event</h2>
            <p>{error}</p>

            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/")}
            >
              Go to Homepage
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (registrationComplete && completedRegistration) {
    return (
      <RegistrationConfirmation
        registration={completedRegistration}
        event={event!}
        onRegisterAnother={() => {
          setRegistrationComplete(false);
          setCompletedRegistration(null);
        }}
      />
    );
  }

  return (
    <div className="registration-page">
      <div className="registration-container">
        <div className="registration-header">
          <div className="event-badge">Event Registration</div>
          <h1 className="event-title">{event?.eventName}</h1>
          {event?.description && (
            <p className="event-description">{event.description}</p>
          )}
        </div>

        <div className="event-details-card">
          <div className="detail-row">
            <div className="detail-content">
              <span className="detail-label">Date & Time</span>
              <span className="detail-value">
                {event && (
                  <>
                    {formatEventDate(event.eventStartDate)} –{" "}
                    {formatEventDate(event.eventEndDate)}
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-content">
              <span className="detail-label">Location</span>
              <span className="detail-value">{event?.location}</span>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-content">
              <span className="detail-label">Availability</span>
              <span className="detail-value">
                {event && (
                  <>
                    {event.maxCapacity - event.currentRegistrations} spots
                    remaining
                    <span className="capacity-info">
                      ({event.currentRegistrations}/{event.maxCapacity}{" "}
                      registered)
                    </span>
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-content">
              <span className="detail-label">Registration Deadline</span>
              <span className="detail-value">
                {event && formatDeadline(event.registrationDeadline)}
              </span>
            </div>
          </div>

          {event?.mealOptionsEnabled && (
            <div className="detail-row">
              <div className="detail-content">
                <span className="detail-label">Meal Options</span>
                <span className="detail-value">Available</span>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div
            className="registration-status-wrap"
            role="alert"
            aria-live="polite"
          >
            <p className="registration-status-text">
              <span className="registration-status-title">
                Registration unavailable
              </span>
              <span className="registration-status-message">{error}</span>
            </p>
          </div>
        )}
        {existingRegistration && !isEditing && (
          <div className="existing-registration-card">
            <h3>You&apos;re already registered!</h3>

            <div className="existing-details">
              <p>
                <strong>Name:</strong> {existingRegistration.userName}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <span
                  className={`status-badge status-${existingRegistration.status.toLowerCase()}`}
                >
                  {existingRegistration.status}
                </span>
              </p>
              <p>
                <strong>Meal Preference:</strong>{" "}
                {existingRegistration.mealPreference}
              </p>
              {event?.additionalGuestsEnabled && (
                <>
                  <p>
                    <strong>Additional Adults:</strong>{" "}
                    {existingRegistration.additionalAdults ?? 0}
                  </p>
                  <p>
                    <strong>Additional Children:</strong>{" "}
                    {existingRegistration.additionalChildren ?? 0}
                  </p>
                </>
              )}
            </div>

            <div className="existing-actions">
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsEditing(true)}
              >
                Update My Response
              </Button>
            </div>
          </div>
        )}

        {event && existingRegistration && isEditing && (
          <RegistrationForm
            eventId={event.eventId}
            mealOptionsEnabled={event.mealOptionsEnabled}
            additionalGuestsEnabled={event.additionalGuestsEnabled ?? false}
            onSuccess={handleRegistrationSuccess}
            isUpdateMode={true}
            initialData={{
              userName: existingRegistration.userName,
              userEmail:
                sessionStorage.getItem("userEmail")?.trim().toLowerCase() || "",
              employeeId: existingRegistration.employeeId || "",
              sector: existingRegistration.sector || "",
              cluster: existingRegistration.cluster || "",
              notes: existingRegistration.notes || "",
              guestCount: existingRegistration.guestCount,
              additionalAdults: existingRegistration.additionalAdults ?? 0,
              additionalChildren: existingRegistration.additionalChildren ?? 0,
              status: existingRegistration.status as any,
              mealPreference: existingRegistration.mealPreference as any,
            }}
            onCancel={() => setIsEditing(false)}
          />
        )}

        {event && !error && !existingRegistration && (
          <RegistrationForm
            eventId={event.eventId}
            mealOptionsEnabled={event.mealOptionsEnabled}
            additionalGuestsEnabled={event.additionalGuestsEnabled ?? false}
            onSuccess={handleRegistrationSuccess}
            initialEmail={
              sessionStorage.getItem("userEmail")?.trim().toLowerCase() || ""
            }
          />
        )}

        <div className="registration-footer">
          <p>Having trouble? Contact the event organizer.</p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;
